import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { Button } from "../../../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import { useEvidenceUpload } from "../../../../../hooks/useEvidenceUpload";
import { useProcedureSession } from "../../../../../hooks/audit/useProcedureSession";
import type {
  EvidenceUploadPreview,
  ZipOrganizationPattern,
  ConfirmSampleMapping,
  DetectedFileNode,
} from "../../../../../models/audit/evidenceUpload";
import { ZipInfoCard } from "./ZipInfoCard";
import { MatchingModeSelect } from "./MatchingModeSelect";
import { FolderMappingList } from "./FolderMappingList";
import { LowConfidenceWarning } from "./LowConfidenceWarning";
import { UnmappedFoldersWarning } from "./UnmappedFoldersWarning";
import { ContentMatchingNotice } from "./ContentMatchingNotice";
import { Progress } from "../../../../../components/ui/progress";
import { Label } from "../../../../../components/ui/label";
import { AlertCircle, Loader2, Upload } from "lucide-react";

// How long a parse runs before the dialog says the wait may be a long one. The
// worker allows ten minutes, so silence before this is unremarkable.
const SLOW_PARSE_HINT_MS = 20_000;

// Shown when the fault is ours, not the auditor's. Compared by identity to
// pick the dialog's copy, so it lives here rather than inline.
const GENERIC_PARSE_ERROR =
  "Something went wrong analyzing this ZIP file. The Midship team has been notified.";

// Lightweight procedure session info passed in by callers. Source rows for
// the currently-selected procedure session are fetched on demand via
// `useProcedureSession(psId)`.
export interface ProcedureSessionInfo {
  id: string;
  name?: string;
}

// Helper type for UI folder representation
export interface FolderInfo {
  path: string;
  name: string;
  files: Array<{ name: string; path: string; sizeBytes: number | null }>;
}

// Extract top-level folders from file tree for UI display.
//
// Zips shaped `wrapper/<per-sample folders>` put the meaningful folders one
// (or more) levels below the root, so we first descend through wrapper
// levels — levels containing exactly one directory and no files — and list
// the immediate child directories of the first non-wrapper level. Folder
// `path` values stay full paths from the zip root, matching the backend's
// full-path `sampleMappings[].folderPath`. A flat zip (per-sample folders at
// the root) hits zero wrapper levels and behaves exactly as before.
function extractFoldersFromTree(tree: DetectedFileNode): FolderInfo[] {
  const folders: FolderInfo[] = [];

  function collectFiles(
    node: DetectedFileNode,
  ): Array<{ name: string; path: string; sizeBytes: number | null }> {
    const files: Array<{
      name: string;
      path: string;
      sizeBytes: number | null;
    }> = [];
    for (const child of node.children) {
      if (child.isDirectory) {
        files.push(...collectFiles(child));
      } else {
        files.push({
          name: child.name,
          path: child.path,
          sizeBytes: child.sizeBytes,
        });
      }
    }
    return files;
  }

  // Descend wrapper chains (exactly one directory, no files). Only descend
  // while the lone directory still contains directories to display, so a zip
  // with a single leaf folder of files keeps listing that folder as before.
  let root = tree;
  while (
    root.children.length === 1 &&
    root.children[0].isDirectory &&
    root.children[0].children.some((child) => child.isDirectory)
  ) {
    root = root.children[0];
  }

  // Get immediate child directories as folders
  for (const child of root.children) {
    if (child.isDirectory) {
      folders.push({
        path: child.path,
        name: child.name,
        files: collectFiles(child),
      });
    }
  }

  return folders;
}

interface EvidenceUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controlSessionId: string;
  /**
   * Every enabled procedure, so the picker can offer the alternatives. NOT the
   * scope the archive is parsed against.
   */
  procedureSessions: ProcedureSessionInfo[];
  /**
   * The one procedure the archive is parsed against, answered before this
   * dialog opened. Parsing against a single procedure is what stops two
   * procedures numbering their samples from 1 making a folder named
   * `Sample 1` ambiguous, and stops a folder settling onto a sample the
   * chosen procedure does not own.
   */
  parseProcedureSessionId: string | null;
  zipFile: File | null;
  onSuccess: () => void;
}

export function EvidenceUploadModal({
  open,
  onOpenChange,
  controlSessionId,
  procedureSessions,
  parseProcedureSessionId,
  zipFile,
  onSuccess,
}: EvidenceUploadModalProps) {
  const [preview, setPreview] = useState<EvidenceUploadPreview | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [selectedPattern, setSelectedPattern] =
    useState<ZipOrganizationPattern>("sample_based");
  // Maps folder path to source row ID (null means no match)
  const [userMappings, setUserMappings] = useState<Map<string, string | null>>(
    new Map(),
  );
  // Track selected procedure session (for multi-session uploads)
  const [selectedProcedureSessionId, setSelectedProcedureSessionId] = useState<
    string | null
  >(null);
  // The file a parse has already been started for, so a re-render mid-parse
  // cannot dispatch a second one for the same archive.
  const parseStartedForRef = useRef<File | null>(null);
  // Stops the in-flight preview poll when the dialog closes.
  const pollAbortRef = useRef<AbortController | null>(null);
  // Which refresh is the current one. Switching procedure again, or closing
  // the dialog, bumps this so an earlier response cannot write its result -
  // the parse effect only runs while `preview` is null, so a resurrected
  // preview means the NEXT zip is silently never parsed.
  const refreshTicketRef = useRef(0);
  // Set once silence starts to look like a hang. Not a counter: the spinner
  // already shows the wait is alive, and a ticking number re-rendered the
  // dialog every second to say nothing new.
  const [parseIsSlow, setParseIsSlow] = useState(false);

  const {
    parseZipFile,
    confirmUpload,
    refreshMappings,
    parseLoading,
    parseState,
    parseAttemptId,
    confirmLoading,
    refreshLoading,
    uploadProgress,
  } = useEvidenceUpload({ controlSessionId });

  // The parse scope is one procedure, answered before this dialog opened.
  // Sending every enabled procedure pooled their samples, which is what let a
  // shared sample number go ambiguous and a folder settle onto another
  // procedure's sample. Falls back to the full list only if no answer arrived,
  // which the caller does not do.
  const procedureSessionIds = useMemo(
    () =>
      parseProcedureSessionId
        ? [parseProcedureSessionId]
        : procedureSessions.map((ps) => ps.id),
    [parseProcedureSessionId, procedureSessions],
  );

  // Which procedure's samples to load. The auditor's own answer wins; the
  // pre-parse choice is the starting point.
  const targetProcedureSessionId =
    selectedProcedureSessionId ??
    parseProcedureSessionId ??
    (procedureSessions.length === 1 ? procedureSessions[0].id : undefined);

  const { data: targetSession, isLoading: samplesLoading } =
    useProcedureSession(targetProcedureSessionId);
  const samples = useMemo(
    () => targetSession?.sourceRows ?? [],
    [targetSession],
  );

  // Shown whenever there is a choice. This also required a backend
  // suggestion, so several procedures with no confident suggestion rendered no
  // picker - and no session meant no samples to map, so every file was
  // skipped.
  const showProcedureSessionSelector = procedureSessions.length > 1;

  // Extract folders from file tree for UI
  const folders = useMemo(() => {
    if (!preview) return [];
    return extractFoldersFromTree(preview.fileTree);
  }, [preview]);

  useEffect(() => {
    if (!parseLoading) {
      setParseIsSlow(false);
      return;
    }
    const timer = setTimeout(() => setParseIsSlow(true), SLOW_PARSE_HINT_MS);
    return () => clearTimeout(timer);
    // Keyed on the attempt as well as the flag: `parseLoading` stays true
    // across a cancel-and-retry, so without it the new wait would inherit the
    // old one's elapsed time and claim "this is taking a while" seconds in.
  }, [parseLoading, parseAttemptId]);

  // Parse file when modal opens with a new file.
  //
  // Guarded on the file rather than on `preview`/`parseError` alone: a parse now
  // spans an S3 upload, a dispatch and a poll, so a re-render mid-flight must
  // not start a second one — that would upload and parse the same archive twice.
  useEffect(() => {
    if (!open || !zipFile || preview || parseError) return;
    if (parseStartedForRef.current === zipFile) return;
    parseStartedForRef.current = zipFile;

    const controller = new AbortController();
    pollAbortRef.current = controller;

    const parseFile = async () => {
      setParseError(null);
      const outcome = await parseZipFile(
        zipFile,
        procedureSessionIds,
        controller.signal,
      );
      // The dialog closed while the worker was still parsing; its state has
      // already been reset and nothing here should resurrect it.
      if (controller.signal.aborted) return;
      if (outcome.ok) {
        const result = outcome.preview;
        setPreview(result);
        // Anything but per-sample folders means "no mapping the modal can
        // express", and they behave the same on confirm. Sending
        // `doc_type_based` is worse than cosmetic: with no doc-type mappings,
        // its confirm branch skips every file silently.
        setSelectedPattern(
          result.detectedPattern === "sample_based"
            ? "sample_based"
            : "unknown",
        );

        // The procedure was chosen before the parse, so detection's own
        // suggestion is not needed and must not overwrite it - the archive was
        // matched against this procedure's samples, and switching is an
        // explicit act that re-runs the parse. Only used as a fallback for a
        // caller that gave no answer.
        setSelectedProcedureSessionId(
          (current) =>
            current ??
            parseProcedureSessionId ??
            result.suggestedProcedureSessionId ??
            (procedureSessions.length === 1 ? procedureSessions[0].id : null),
        );

        // Initialize user mappings from server suggestions
        const initialMappings = new Map<string, string | null>();
        for (const mapping of result.sampleMappings) {
          initialMappings.set(mapping.folderPath, mapping.suggestedSourceRowId);
        }
        setUserMappings(initialMappings);
      } else {
        // A reason means it is theirs to fix; without one it is ours.
        setParseError(outcome.reason ?? GENERIC_PARSE_ERROR);
      }
    };

    parseFile();
  }, [
    open,
    zipFile,
    procedureSessionIds,
    parseProcedureSessionId,
    procedureSessions,
    parseZipFile,
    preview,
    parseError,
  ]);

  // Drop everything the closing dialog was holding, including any parse still
  // being polled for. The worker finishes either way; nobody is waiting on it.
  const resetState = useCallback(() => {
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    parseStartedForRef.current = null;
    refreshTicketRef.current += 1;
    setPreview(null);
    setParseError(null);
    setUserMappings(new Map());
    setSelectedProcedureSessionId(null);
  }, []);

  // Handle dialog close
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        resetState();
      }
      onOpenChange(isOpen);
    },
    [onOpenChange, resetState],
  );

  const handleClose = useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [onOpenChange, resetState]);

  // Handle procedure session change - refresh mappings from backend
  const handleProcedureSessionChange = useCallback(
    async (newSessionId: string) => {
      if (!preview || newSessionId === selectedProcedureSessionId) return;

      const previousSessionId = selectedProcedureSessionId;
      const ticket = refreshTicketRef.current + 1;
      refreshTicketRef.current = ticket;

      setSelectedProcedureSessionId(newSessionId);

      // Call refresh endpoint to get new mappings for the selected session
      const result = await refreshMappings(preview.uploadId, newSessionId);

      // Superseded, or the dialog closed while this was in flight. Writing
      // either the id or the mappings now would describe a procedure that is
      // no longer on screen.
      if (refreshTicketRef.current !== ticket) return;

      if (!result) {
        // The id and the mappings must always describe the same procedure.
        // Keeping the new id with the old mappings sends the old procedure's
        // sample ids up against the new one.
        setSelectedProcedureSessionId(previousSessionId);
        return;
      }

      setPreview(result);

      // Re-initialize user mappings from new server suggestions
      const newMappings = new Map<string, string | null>();
      for (const mapping of result.sampleMappings) {
        newMappings.set(mapping.folderPath, mapping.suggestedSourceRowId);
      }
      setUserMappings(newMappings);
    },
    [preview, selectedProcedureSessionId, refreshMappings],
  );

  const handlePatternChange = useCallback(
    (pattern: ZipOrganizationPattern) => {
      setSelectedPattern(pattern);
      // When switching back to sample_based, restore mappings from server suggestions
      if (pattern === "sample_based" && preview) {
        const restoredMappings = new Map<string, string | null>();
        for (const mapping of preview.sampleMappings) {
          restoredMappings.set(
            mapping.folderPath,
            mapping.suggestedSourceRowId,
          );
        }
        setUserMappings(restoredMappings);
      } else {
        setUserMappings(new Map());
      }
    },
    [preview],
  );

  const handleMappingChange = useCallback(
    (folderPath: string, sourceRowId: string | null) => {
      setUserMappings((prev) => {
        const next = new Map(prev);
        next.set(folderPath, sourceRowId);
        return next;
      });
    },
    [],
  );

  // The one place a folder's sample is decided, so the table, the counts and
  // the confirmed payload cannot disagree.
  //
  // A mapping can name a sample this procedure does not own: folder settlement
  // runs at preview time against every selected procedure's rows pooled
  // together, and a failed refresh after switching procedure leaves the old
  // procedure's ids in place. Such an id is unresolvable here, and treating it
  // as "no sample" has to happen once, for all three consumers. Deriving it
  // only for display is what let the table read "No match" on every row while
  // Confirm still submitted the stale ids.
  const resolvedMappings = useMemo(() => {
    const sampleIds = new Set(samples.map((sample) => sample.id));
    const resolved = new Map<string, string | null>();
    for (const folder of folders) {
      const proposed = userMappings.get(folder.path) ?? null;
      resolved.set(
        folder.path,
        proposed !== null && sampleIds.has(proposed) ? proposed : null,
      );
    }
    return resolved;
  }, [folders, userMappings, samples]);

  // Files a folder mapping pins to a sample. `folder.files` is collected
  // recursively, so a mapped folder counts its whole subtree.
  const mappedFileCount = useMemo(() => {
    let count = 0;
    for (const folder of folders) {
      if (resolvedMappings.get(folder.path) !== null) {
        count += folder.files.length;
      }
    }
    return count;
  }, [folders, resolvedMappings]);

  const handleConfirm = useCallback(async () => {
    if (!preview) return;

    // Build sample mappings from user selections. Only include non-null
    // mappings for folders actually displayed in the dialog: the backend may
    // suggest mappings for undisplayed folders (e.g. a wrapper folder), and
    // with ancestor matching such a mapping would silently route every file
    // in the zip to a single sample. Dropped mappings fall back to
    // skipped-and-visible, the safe failure mode.
    const sampleMappings: ConfirmSampleMapping[] = [];
    for (const [folderPath, sourceRowId] of resolvedMappings.entries()) {
      if (sourceRowId !== null) {
        sampleMappings.push({
          folder_path: folderPath,
          source_row_id: sourceRowId,
        });
      }
    }

    const result = await confirmUpload(
      preview.uploadId,
      selectedPattern,
      // `targetProcedureSessionId`, not the raw selection, so a single-
      // procedure control names it rather than letting the backend guess.
      targetProcedureSessionId ?? null,
      sampleMappings,
    );

    // Both non-null outcomes hand off to the page's processing status: the
    // documents are being created on a worker either way, and the counts arrive
    // as a toast when it finishes. "indeterminate" means the dispatch itself was
    // killed at the network/gateway level, so we cannot tell whether it landed —
    // close all the same rather than leave a re-armed Confirm button whose
    // second click would double-process the files.
    if (result !== null) {
      handleClose();
      onSuccess();
    }
  }, [
    preview,
    selectedPattern,
    targetProcedureSessionId,
    resolvedMappings,
    confirmUpload,
    handleClose,
    onSuccess,
  ]);

  const isLoading = parseLoading || refreshLoading;
  // Both mean "no folder mapping" - every file goes to session-wide AI
  // matching - so neither has a folder table to show.
  const isUnknownPattern = selectedPattern === "unknown";
  const mapsWholeArchive = isUnknownPattern;
  // A control with several procedures needs one chosen first. Otherwise the
  // request omits the id and the backend takes the first enabled procedure,
  // so the files land somewhere nobody picked.
  const needsProcedureChoice =
    showProcedureSessionSelector && !selectedProcedureSessionId;
  // `samplesLoading` included: until the samples arrive, every mapping is
  // unresolvable, so the table reads "No match" on every row and confirming
  // would submit nothing. If `useProcedureSession` errors, `samples` settles
  // empty and that state is permanent.
  const canConfirm =
    !isLoading &&
    preview !== null &&
    !needsProcedureChoice &&
    !(samplesLoading && !mapsWholeArchive);
  const showLowConfidenceWarning =
    preview !== null && preview.patternConfidence < 0.7;
  // Everything no mapping placed: files under an unmapped folder, and files
  // at the archive root or a wrapper level that belong to no listed folder.
  // By subtraction, because walking the folder list missed the root ones.
  const unplacedFileCount = preview
    ? preview.totalFileCount - mappedFileCount
    : 0;
  const showUnmappedWarning = unplacedFileCount > 0 && !mapsWholeArchive;

  // Get display name for a procedure session
  const getProcedureSessionLabel = useCallback(
    (sessionId: string) => {
      const session = procedureSessions.find((ps) => ps.id === sessionId);
      return session?.name ?? `Session ${sessionId.slice(0, 8)}`;
    },
    [procedureSessions],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            ZIP detected — How should we match these files?
          </DialogTitle>
          <DialogDescription>
            {parseError
              ? "There was a problem analyzing the ZIP file."
              : preview
                ? `We found ${folders.length} folder${folders.length !== 1 ? "s" : ""} with ${preview.totalFileCount} file${preview.totalFileCount !== 1 ? "s" : ""}`
                : uploadProgress !== null
                  ? "Uploading ZIP file…"
                  : "Analyzing ZIP file structure..."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 py-4">
          {uploadProgress !== null ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Upload className="h-10 w-10 text-stone-400" aria-hidden="true" />
              <div className="w-full max-w-sm space-y-2">
                <Progress value={uploadProgress} />
                <p className="text-sm text-stone-500 text-center">
                  Uploading… {uploadProgress}%
                </p>
              </div>
            </div>
          ) : parseLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4 text-center">
              <Loader2
                className="h-10 w-10 animate-spin text-stone-400"
                aria-hidden="true"
              />
              <p
                className="text-sm font-medium text-stone-900"
                aria-live="polite"
              >
                {parseState === "queued"
                  ? "Queued — starting shortly…"
                  : "Reading the ZIP and matching folders to samples…"}
              </p>
              {parseIsSlow && (
                <p className="text-sm text-stone-500 max-w-sm">
                  Large zips can take a few minutes. You can cancel and upload
                  again at any point.
                </p>
              )}
            </div>
          ) : parseError ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle
                className="h-12 w-12 text-red-500 mb-4"
                aria-hidden="true"
              />
              <h3 className="text-lg font-medium text-stone-900 mb-2">
                {parseError === GENERIC_PARSE_ERROR
                  ? "Failed to analyze ZIP file"
                  : "This ZIP file can't be processed"}
              </h3>
              <p className="text-sm text-stone-600 max-w-md whitespace-pre-wrap">
                {parseError}
              </p>
              {parseError !== GENERIC_PARSE_ERROR && (
                <p className="text-sm text-stone-500 max-w-md mt-3">
                  Remove or convert those files and upload the ZIP again.
                </p>
              )}
            </div>
          ) : preview ? (
            <>
              <ZipInfoCard
                fileName={preview.zipFilename}
                folderCount={folders.length}
                fileCount={preview.totalFileCount}
                fileSize={preview.totalSizeBytes}
              />

              {/* Not gated on a selection existing. Detection only suggests a
                  session when it is confident, so requiring one hid the picker
                  in exactly the case the auditor needs it - several procedures
                  and no confident guess - and the confirm then fell back to
                  whichever procedure the backend happened to list first. */}
              {showProcedureSessionSelector && (
                <div className="flex items-center gap-3">
                  <Label
                    htmlFor="procedure-select"
                    className="text-sm font-medium text-stone-700"
                  >
                    Procedure:
                  </Label>
                  <Select
                    value={selectedProcedureSessionId ?? ""}
                    onValueChange={handleProcedureSessionChange}
                    disabled={confirmLoading || refreshLoading}
                  >
                    <SelectTrigger id="procedure-select" className="w-64">
                      <SelectValue placeholder="Choose a procedure" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* No confidence badge here. It read as detection's
                          opinion of the procedure, but `refresh_mappings` sets
                          `procedure_session_confidence` to 1.0 whenever the
                          auditor picks one - so after any change it showed
                          "100% match" purely because they had chosen it, which
                          is their own input played back as a score. The folder
                          rows carry real per-match confidence; the procedure
                          is a straight choice and needs no number. */}
                      {procedureSessions.map((session) => (
                        <SelectItem key={session.id} value={session.id}>
                          {getProcedureSessionLabel(session.id)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {refreshLoading && (
                    <span className="text-sm text-stone-500">
                      Refreshing mappings...
                    </span>
                  )}
                </div>
              )}

              <MatchingModeSelect
                value={selectedPattern}
                onChange={handlePatternChange}
                disabled={confirmLoading || refreshLoading}
              />

              {showLowConfidenceWarning && (
                <LowConfidenceWarning
                  confidence={preview.patternConfidence}
                  reasoning={preview.patternReasoning}
                />
              )}

              {!mapsWholeArchive && (
                <div className="flex-1 overflow-y-auto min-h-0 border rounded-md">
                  <FolderMappingList
                    folders={folders}
                    samples={samples}
                    resolvedMappings={resolvedMappings}
                    userMappings={userMappings}
                    serverMappings={preview.sampleMappings}
                    onMappingChange={handleMappingChange}
                    disabled={confirmLoading || refreshLoading}
                    samplesLoading={samplesLoading}
                  />
                </div>
              )}

              {/* One of the two always renders, so the dialog is never silent
                  about what will happen to the files it just listed. */}
              {mapsWholeArchive ? (
                <ContentMatchingNotice fileCount={preview.totalFileCount} />
              ) : (
                showUnmappedWarning && (
                  <UnmappedFoldersWarning fileCount={unplacedFileCount} />
                )
              )}
            </>
          ) : null}
        </div>

        <DialogFooter>
          {/* Enabled during upload and parse on purpose: those span an S3
              upload plus a poll that the worker allows 15 minutes for, and
              Escape already closes the dialog, so a disabled button only
              contradicted the dialog it sat in. Closing aborts the upload. */}
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={confirmLoading}
          >
            Cancel
          </Button>
          {!parseError && (
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm || confirmLoading}
              loading={confirmLoading}
            >
              {/* The whole archive, not just the mapped part. Counting
                  `mappedFileCount` was right when an unmapped file was
                  discarded; every file is now uploaded, so a zip of one mapped
                  folder and two loose files offered "1 file" and did three. */}
              {preview
                ? `Confirm & Process ${preview.totalFileCount} file${preview.totalFileCount !== 1 ? "s" : ""}`
                : "Confirm & Process"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
